import React, { useEffect, useState, useCallback } from "react";
import debounce from "lodash/debounce";
import { toast } from "react-hot-toast";
import { emitCustomEvent, useCustomEventListener } from "react-custom-events";
import FormRegion from "../../components/molecules/FormRegion";
import Region from "../../api/Region";
import { useTranslation } from "react-i18next";
import DeleteModal from "../../components/modals/DeleteModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "../../components/organisms/Layout";
import { FormModal } from "../../components/organisms/FormModal";
import PaginationView from "../../components/molecules/Pagination";
import SearchBar from "../../components/atoms/SearchBar";
import useQuery from "../../hooks/useQuery";
import Table from "../../components/atoms/Tables/Table";
import { addIcon } from "../../assets/images";
import { encodeIds } from "../../utils";

const LocationsTable = ({ locations }) => {
  const navigate = useNavigate();

  return (
    <div className="min-w-full mt-1 mb-3">
      <div className="font-bold">Locations</div>
      {locations &&
        locations.map((location) => {
          return (
            <div
              key={location.id}
              className="cursor-pointer hover:bg-gray-100 p-2"
              onClick={() => {
                navigate(`/companies/locations/${encodeIds(location.id)}/edit`);
              }}
            >
              {location.name}
            </div>
          );
        })}
    </div>
  );
};

const ShowSingleRow = ({
  region,
  setEditRegion,
  setShowModal,
  setRegionToBeDeleted,
}) => {
  const [showLocations, setShowLocations] = useState(false);
  return (
    <>
      <tr
        key={region.id}
        className="hover:bg-gray-50 transition-colors duration-150"
        onClick={() => setShowLocations(!showLocations)}
      >
        <td className="px-6 py-4 text-sm">
          <div className="font-semibold text-gray-900 break-words">
            {region.name}
          </div>
        </td>
        <td className="whitespace-nowrap px-6 py-4 text-sm">
          <div className="text-gray-700">
            {region.company.name}
          </div>
        </td>
        <td className="whitespace-nowrap px-6 py-4 text-sm">
          <div className="text-gray-700">
            {region.owners && region.owners.length > 0
              ? `${region.owners[0].first_name} ${region.owners[0].last_name}`
              : "-"}
          </div>
        </td>
        <td className="whitespace-nowrap px-6 py-4 text-sm">
          <div className="text-gray-700">
            {region.locations.length}
          </div>
        </td>
        <td className="whitespace-nowrap px-2 sm:px-3 py-2.5 sm:py-3 text-sm md:text-base">
          <div className="flex items-center gap-4">
            <button
              className="text-primary-600 hover:text-primary-900 font-medium hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                setEditRegion({
                  ...region,
                  company: {
                    id: region.company?.id,
                    name: region.company?.name,
                  },
                });
                setShowModal(true);
              }}
            >
              Edit
            </button>
            <button
              className="text-primary-600 hover:text-primary-900 font-medium hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                setRegionToBeDeleted(region);
              }}
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
      {showLocations && (
        <div className="p-4">
          <LocationsTable locations={region?.locations} />
        </div>
      )}
    </>
  );
};

const Regions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useQuery();

  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editRegion, setEditRegion] = useState(null);
  const [metaData, setMetaData] = useState({});
  const [regionToBeDeleted, setRegionToBeDeleted] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchRegion, setSearchRegion] = useState("");
  const [filteredRegions, setFilteredRegions] = useState([]);
  const [pageLimit, setPagelimit] = useState(query.get("pageSize") || 50);

  useEffect(() => {
    document.title = "HeyHire Portal-Regions";
  }, []);

  useEffect(() => {
    loadRegions();
  }, [query]);

  useCustomEventListener("regionChanged", () => {
    setShowModal(false);
    setRegionToBeDeleted(null);
    loadRegions();
  });

  useCustomEventListener("closeModal", () => {
    setShowModal(false);
    setRegionToBeDeleted(null);
  });

  const debouncedUpdateSearch = useCallback(
    debounce((txt) => {
      // Create a new object from current search params
      const newParams = { ...Object.fromEntries(searchParams) };
      
      // Reset to page 1 when searching
      newParams.page = 1;
      
      // Add or remove search parameter based on value
      if (txt && txt.trim() !== '') {
        newParams.search = txt.trim();
      } else {
        delete newParams.search;
      }
      
      setSearchParams(newParams);
    }, 300),
    [searchParams]
  );

  const searchRegionName = (txt) => {
    setSearchRegion(txt);
    debouncedUpdateSearch(txt);
  };

  const loadRegions = () => {
    setLoading(true);
    const page = query.get("page") || 1;
    const sort = query.get("sort") || "";
    const limit = query.get("limit") || pageLimit;
    const search = query.get("search") || "";
    
    const sortData = sort ? `sort=${sort}&` : "";
    const pageData = page ? `page=${page}` : "";
    const searchData = search ? `&search=${encodeURIComponent(search)}` : "";
    const queryData = sortData + pageData + `&limit=${limit}` + searchData;
    
    // Update the search input field when loading from URL
    setSearchRegion(search);
    
    Region.getAll(queryData)
      .then((response) => {
        setRegions(response.data.data);
        setFilteredRegions(response.data.data);
        setMetaData(response.data.meta);
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error(t("common.something-went-wrong"));
        }
      })
      .finally(() => setLoading(false));
  };

  const addEditRegion = (values) => {
    setLoading(true);
    const payload = {
      name: values.name,
      company_id: values.company_id?.value || values.company_id,
    };

    if (!payload.name || !payload.company_id) {
      toast.error(t("common.please-fill-all-required-fields"));
      setLoading(false);
      return;
    }

    const request = editRegion
      ? Region.update(editRegion.id, payload)
      : Region.create(payload);

    request
      .then((response) => {
        toast.success(
          editRegion ? t("regions.region-updated") : t("regions.region-added")
        );
        setShowModal(false);
        setEditRegion(null);
        loadRegions();
      })
      .catch((error) => {
        console.error("API request failed:", error);
        if (error.response && error.response.status === 422) {
          toast.error(
            error.response.data.message || t("common.validation-error")
          );
        } else {
          if (error?.response?.status !== 403) {
            toast.error(t("common.something-went-wrong-please-try-again"));
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlecPageChange = (currentPage, pageSize) => {
    setPagelimit(pageSize);
    // Create a new object from current search params
    const newParams = { ...Object.fromEntries(searchParams) };
    
    newParams.page = currentPage;
    newParams.limit = pageSize;
    
    // Only include search if it has a value
    if (searchRegion && searchRegion.trim() !== '') {
      newParams.search = searchRegion.trim();
    } else {
      delete newParams.search;
    }
    
    setSearchParams(newParams);
  };

  return (
    <DashboardLayout darmo={true}>
      {showModal && (
        <FormModal
          visible={showModal}
          onClose={() => {
            setShowModal(false);
            setEditRegion(null);
          }}
          title={
            editRegion ? t("regions.edit-region") : t("regions.add-a-region")
          }
          onSubmit={(values) => {
            addEditRegion(values);
          }}
        >
          <FormRegion region={editRegion} />
        </FormModal>
      )}
      {regionToBeDeleted && (
        <DeleteModal
          title="Delete Region"
          name="Region"
          deleteRequest={() => Region.deleteById(regionToBeDeleted.id)}
          onSuccess={() => emitCustomEvent("regionChanged")}
        />
      )}

      <div className="pb-12">
        <Table
          isLoading={loading}
          mainHeader="Regions"
          searchBar={
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full sm:justify-end">
              <div className="w-full sm:w-[500px]">
                <SearchBar
                  searchValue={searchRegion}
                  placeholder="Search by region name..."
                  searchFunction={(value) => searchRegionName(value)}
                />
              </div>
            </div>
          }
          maxHeight="calc(100vh - 250px)"
          headers={
            !loading && filteredRegions?.length === 0
              ? ["No data available"]
              : [
                  t("regions.region-name"),
                  t("common.company-name"),
                  t("regions.owner"),
                  t("regions.no-of-locations"),
                  "Actions",
                ]
          }
          data={filteredRegions?.map((region) => (
            <ShowSingleRow
              key={region.id}
              region={region}
              setEditRegion={setEditRegion}
              setShowModal={setShowModal}
              setRegionToBeDeleted={setRegionToBeDeleted}
            />
          ))}
          buttonData={
            <button
              onClick={() => navigate("add")}
              className="w-full sm:w-auto flex-shrink-0 cursor-pointer flex justify-center items-center rounded-lg bg-primary-500 px-6 h-10 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 transition-all duration-200"
            >
              <img src={addIcon} alt="add" className="mr-2 w-4 h-4" />
              Add Region
            </button>
          }
        />
        <div className="flex w-full h-20 justify-center md:justify-end items-center px-4">
          {!loading && metaData?.total > 0 && (
            <PaginationView
              current={metaData?.current_page}
              next={metaData?.to}
              pageSize={metaData?.per_page}
              total={metaData?.total}
              onChange={handlecPageChange}
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Regions;
